import { Avatar, Box, Chip, Stack, Typography } from "@mui/material";
import TagIcon from '@mui/icons-material/Tag';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import helpers from "../../../hooks/HelperFunctions";

function DetailPill({ icon, text, href }) {
    return (
        <Stack
            component={href ? 'a' : 'div'}
            href={href}
            target={href ? '_blank' : undefined}
            rel={href ? 'noopener noreferrer' : undefined}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
                bgcolor: 'rgba(255,82,0,0.05)',
                border: '1px solid rgba(255,82,0,0.14)',
                borderRadius: '20px',
                px: 1.25,
                py: 0.4,
                textDecoration: 'none',
                cursor: href ? 'pointer' : 'default',
                '&:hover': href ? { bgcolor: 'rgba(255,82,0,0.1)' } : undefined,
            }}
        >
            <Box sx={{ color: '#FF5200', display: 'flex', alignItems: 'center' }}>{icon}</Box>
            <Typography
                sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'Quicksand', color: '#374151' }}
            >
                {text}
            </Typography>
        </Stack>
    );
}

function CreatorCard({ creator, relationship }) {
    const initials = (creator?.name || creator?.username || '??').substr(0, 2).toUpperCase();
    // creator-payment-history returns the raw RapidInfluencer doc (or a lean
    // fallback) — categoryName/category_name no longer exist there, only the
    // consolidated `categories` array. Prefer that, fall back to the legacy
    // string shapes in case an older/back-compat route still sends them.
    const category = Array.isArray(creator?.categories) && creator.categories.length
        ? creator.categories.join(', ')
        : typeof creator?.categoryName === 'string'
            ? creator.categoryName
            : creator?.category_name;

    return (
        <Box
            sx={{
                borderRadius: 4,
                background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF8F5 100%)',
                p:{
                    xs:2,
                    sm:3,
                    md:'24px 28px'
                },
                border: '1px solid rgba(255,82,0,0.12)',
                boxShadow: '0 4px 24px rgba(255,82,0,0.06)',
                display: 'flex',
                flexDirection:{
                    xs:'column',
                    sm:'row'
                },
                alignItems:{
                    xs:'center',
                    sm:'center'
                },
                textAlign:{
                    xs:'center',
                    sm:'left'
                },
                gap: {
                    xs:2,
                    sm:3
                },
            }}
        >
            {/* Avatar with orange gradient ring */}
            <Box
                sx={{
                    flexShrink: 0,
                    width: {
                        xs:64,
                        sm:72,
                        md:76
                    },
                    height: {
                        xs:64,
                        sm:72,
                        md:76,
                    },
                    borderRadius: '50%',
                    padding: '2.5px',
                    background: 'linear-gradient(135deg, #FF5200 0%, #FF8C42 100%)',
                    boxShadow: '0 4px 16px rgba(255,82,0,0.28)',
                }}
            >
                <Avatar
                    src={creator?.picture || creator?.instaProfilePicUrlHd}
                    sx={{
                        width: '100%',
                        height: '100%',
                        bgcolor: '#1C1C2E',
                        fontSize: {
                            xs:18,
                            sm:20,
                            md:22
                        },
                        fontWeight: 800,
                        fontFamily: 'Quicksand',
                        border: '2.5px solid #FFFFFF',
                        color: '#FFFFFF',
                    }}
                >
                    {initials}
                </Avatar>
            </Box>

            {/* Details */}
            <Box sx={{ flex: 1, minWidth: 0 ,width:'100%'}}>
                <Stack direction={{xs:'column',sm:'row'}} alignItems="center" spacing={1} mb={1}>
                    <Typography
                        sx={{ fontSize: {
                            xs:18,
                            sm:20,
                            md:22
                        }, fontWeight: 800, fontFamily: 'Quicksand', color: '#0F172A',wordBreak:'break-word' }}
                    >
                        {creator?.name || 'N.A'}
                    </Typography>
                    {category && (
                        <Chip
                            label={category}
                            size="small"
                            sx={{
                                maxWidth:{
                                    xs:'100%',
                                    sm:'fit-content'
                                },
                                '& .MuiChip-label':{
                                    overflow:'hidden',
                                    textOverflow:'ellipsis',
                                    whiteSpace:'nowrap'
                                },
                                bgcolor: 'rgba(255,82,0,0.08)',
                                color: '#FF5200',
                                fontFamily: 'Quicksand',
                                fontWeight: 700,
                                fontSize: 10,
                                height: 22,
                                border: '1px solid rgba(255,82,0,0.22)',
                            }}
                        />
                    )}
                </Stack>

                <Stack direction="row" flexWrap="wrap" justifyContent={{xs:'center',sm:'flex-start'}} gap={0.75}>
                    <DetailPill
                        icon={<TagIcon sx={{ fontSize: 13 }} />}
                        text={`@${creator?.username}`}
                        href={creator?.username ? `${process.env.REACT_APP_INSTAGRAM_BASE}${creator.username}` : undefined}
                    />
                    <DetailPill
                        icon={<PeopleAltIcon sx={{ fontSize: 13 }} />}
                        text={`${helpers.formatNumber(creator?.instaFollowers, false)} followers`}
                    />
                    {creator?.average_views && (
                        <DetailPill
                            icon={<ElectricBoltIcon sx={{ fontSize: 13 }} />}
                            text={`${creator.average_views} avg views`}
                        />
                    )}
                    {relationship && (
                        <DetailPill
                            icon={<AccessTimeIcon sx={{ fontSize: 13 }} />}
                            text={`${relationship} relationship`}
                        />
                    )}
                </Stack>
            </Box>
        </Box>
    );
}

export default CreatorCard;
